import { forwardRef } from 'react'
import type { ElementRef, ComponentPropsWithoutRef, CSSProperties } from 'react'
import { clsx } from 'clsx'

interface ListItemCssProperties extends CSSProperties {
  '--icon-color'?: string
}

import styles from './List.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'
import type { IconProps } from '../icons'

type ListElement = ElementRef<'ul'>
type ListProps = ComponentPropsWithoutRef<'ul'>

/**
 * Unordered list, renders as an `<ul>` element, supports all native attributes
 */
const List = forwardRef<ListElement, ListProps>(
  (props: ListProps, forwardedRef) => (
    <ul
      {...props}
      ref={forwardedRef}
      className={clsx(styles.unorderedList, props.className)}
    />
  ),
)

List.displayName = 'List'

type ListItemElement = ElementRef<'li'>
type ListItemProps = ComponentPropsWithoutRef<'li'> & {
  /**
   * Set color variation of the list
   */
  color?: 'neutral' | 'primary' | 'success' | 'critical' | 'warning'
  /**
   * Set custom icon for the list item or declare no bullet styling at all
   */
  icon?: React.ComponentType<IconProps> | false
}

const colorMap = {
  neutral: 'var(--color-border-neutral)',
  primary: 'var(--color-bg-brand)',
  success: 'var(--color-bg-success)',
  critical: 'var(--color-bg-critical)',
  warning: 'var(--color-bg-warning)',
}

const ListItem = forwardRef<ListItemElement, ListItemProps>(
  (
    { className, color, children, icon, style, ...props }: ListItemProps,
    forwardedRef,
  ) => {
    const Icon = icon
    const itemStyle: ListItemCssProperties = {
      ...style,
      '--icon-color': color ? colorMap[color] : undefined,
    }

    return (
      <li
        {...props}
        ref={forwardedRef}
        className={clsx(
          styles.item,
          icon === undefined && styles.bulletItem,
          className,
        )}
        style={itemStyle}
      >
        {Icon && (
          <span className={clsx(iconSlotStyles.firstLine, styles.iconSlot)}>
            <Icon className={styles.icon} />
          </span>
        )}
        {children}
      </li>
    )
  },
)

ListItem.displayName = 'List.Item'

const ListObject = Object.assign(List, {
  Item: ListItem,
})

export { ListObject as List, ListItem }
export type { ListProps, ListItemProps }
